import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ThemeService } from '@services';
import { ToastService } from '@services';
import { Router, RouterOutlet } from '@angular/router';
import { appstraxAuth } from '@appstrax/services/auth';
import { ExecutionFabComponent } from '@components';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { Unsubscribe } from '@appstrax/services/auth/services/auth.service';
import { UserFacade } from '@state';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ExecutionFabComponent, ToastContainerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'machine-site';
  public loading = signal<boolean>(false);

  private unsubscribe?: Unsubscribe;

  constructor(
    private theme: ThemeService,
    private toast: ToastService,
    private router: Router,
    private userFacade: UserFacade,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.checkAuthentication();

      // Single source of truth for the user slice: fires immediately with the
      // current auth user and again on every login/logout.
      this.unsubscribe = appstraxAuth.subscribeToUserChanges((user) => {
        if (user) {
          this.userFacade.setCurrentFromAuthUser(user);
        } else {
          this.userFacade.clear();
          if (!this.isPublicRoute()) {
            void this.router.navigate(['/login']);
          }
        }
      });
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to initialize application', 'App');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
  }

  private async checkAuthentication(): Promise<void> {
    this.loading.set(true);
    const isAuthenticated = await appstraxAuth.isAuthenticated();
    this.loading.set(false);

    if (!isAuthenticated && !this.isPublicRoute()) {
      void this.router.navigate(['/login']);
    }
  }

  /** Paths that do not require a session (see app.routes). */
  private isPublicRoute(): boolean {
    const path = (this.router.url ?? '').split('?')[0] || '/';
    if (path === '/' || path === '') return true;
    const publicPaths = new Set([
      '/login',
      '/sign-up',
      '/verify-email',
      '/create-project',
      '/forgot-password',
      '/create-organization',
    ]);
    return publicPaths.has(path);
  }
}
